import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Camera, User, Ruler, Check, AlertCircle, Loader2, Save, Users, Download, Printer } from "lucide-react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper component for displaying measurements
const MeasurementDisplay = ({ label, value, unit = '"' }) => (
  <div className="flex justify-between items-center py-2 border-b border-white/5">
    <span className="text-sm text-muted-foreground">{label}</span>
    <span className="font-mono text-primary">{value}{unit}</span>
  </div>
);

const AIMeasurementPage = () => {
  const [step, setStep] = useState(1);
  const [height, setHeight] = useState({ feet: "", inches: "" });
  const [weight, setWeight] = useState("");
  const [fitPreference, setFitPreference] = useState("Classic");
  const [frontImage, setFrontImage] = useState(null);
  const [sideImage, setSideImage] = useState(null);
  const [backImage, setBackImage] = useState(null);
  const [frontPreview, setFrontPreview] = useState(null);
  const [sidePreview, setSidePreview] = useState(null);
  const [backPreview, setBackPreview] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [results, setResults] = useState(null);
  const [showGuide, setShowGuide] = useState(false);
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [showSaveToClient, setShowSaveToClient] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const frontInputRef = useRef(null);
  const sideInputRef = useRef(null);
  const backInputRef = useRef(null);
  const navigate = useNavigate();

  // Fetch clients for save to CRM option
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const res = await axios.get(`${API}/crm/clients`);
        setClients(res.data || []);
      } catch (error) {
        console.error("Failed to fetch clients:", error);
      }
    };
    fetchClients();
  }, []);

  const handleImageUpload = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.match(/^image\/(jpeg|jpg|png|webp)$/)) {
      toast.error("Please upload a JPEG, PNG, or WebP image");
      return;
    }
    
    // Read file as base64
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target.result.split(",")[1];
      const preview = event.target.result;
      
      if (type === "front") {
        setFrontImage(base64);
        setFrontPreview(preview);
      } else if (type === "side") {
        setSideImage(base64);
        setSidePreview(preview);
      } else {
        setBackImage(base64);
        setBackPreview(preview);
      }
    };
    reader.readAsDataURL(file);
  };

  const getTotalHeightInches = () => {
    const feet = parseInt(height.feet) || 0;
    const inches = parseInt(height.inches) || 0;
    return (feet * 12) + inches;
  };

  const canProceedStep1 = getTotalHeightInches() >= 48; // At least 4 feet
  const canProceedStep2 = frontImage && sideImage;

  const handleAnalyze = async () => {
    if (!frontImage || !sideImage) {
      toast.error("Please upload front and side photos");
      return;
    }
    
    setAnalyzing(true);
    try {
      const response = await axios.post(`${API}/ai-measurement/analyze`, {
        front_image_base64: frontImage,
        side_image_base64: sideImage,
        back_image_base64: backImage || null,
        height_inches: getTotalHeightInches(),
        weight_lbs: weight ? parseFloat(weight) : null,
        fit_preference: fitPreference,
        client_id: selectedClientId && selectedClientId !== "none" ? selectedClientId : null,
      });
      
      setResults(response.data);
      setStep(3);
      toast.success("Measurements analyzed successfully!");
    } catch (error) {
      console.error("Analysis error:", error);
      toast.error(error.response?.data?.detail || "Analysis failed. Please try again.");
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSaveToClient = async () => {
    if (!selectedClientId || selectedClientId === "none" || !results) {
      toast.error("Please select a client");
      return;
    }
    
    setSaving(true);
    try {
      await axios.put(`${API}/crm/clients/${selectedClientId}`, {
        ai_measurements: results,
        last_ai_measurement_date: new Date().toISOString()
      });
      toast.success("Measurements saved to client profile!");
      setShowSaveToClient(false);
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save to client profile");
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const getConfidenceBadgeColor = (level) => {
    switch (level) {
      case "High": return "bg-green-500/20 text-green-400 border-green-500/30";
      case "Medium": return "bg-yellow-500/20 text-yellow-400 border-yellow-500/30";
      case "Low": return "bg-red-500/20 text-red-400 border-red-500/30";
      default: return "bg-gray-500/20 text-gray-400 border-gray-500/30";
    }
  };

  return (
    <div className="min-h-screen pt-20 pb-16">
      {/* Header */}
      <section className="py-8 border-b border-white/5">
        <div className="max-w-[800px] mx-auto px-6 md:px-12">
          <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
            <ArrowLeft size={16} className="mr-2" /> Back
          </Button>
          <div className="flex items-center gap-3 mb-2">
            <Ruler className="text-primary" size={28} />
            <h1 className="font-serif text-3xl tracking-tight">AI Body Measurement</h1>
          </div>
          <p className="text-muted-foreground">
            Professional made-to-measure suit measurements from photos
          </p>
        </div>
      </section>

      {/* Progress Steps */}
      <section className="py-6 border-b border-white/5">
        <div className="max-w-[800px] mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between">
            {[
              { num: 1, label: "Your Info" },
              { num: 2, label: "Photos" },
              { num: 3, label: "Results" }
            ].map((s, i) => (
              <div key={s.num} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${
                  step >= s.num ? "bg-primary text-primary-foreground" : "bg-white/10 text-muted-foreground"
                }`}>
                  {step > s.num ? <Check size={16} /> : s.num}
                </div>
                <span className={`ml-2 text-sm ${step >= s.num ? "text-foreground" : "text-muted-foreground"}`}>
                  {s.label}
                </span>
                {i < 2 && <div className={`w-16 md:w-24 h-px mx-4 ${step > s.num ? "bg-primary" : "bg-white/10"}`} />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-8">
        <div className="max-w-[800px] mx-auto px-6 md:px-12">
          {/* Step 1: Basic Info */}
          {step === 1 && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="bg-card border border-white/10 p-6 rounded-lg mb-6">
                <h2 className="font-serif text-xl mb-6">Your Information</h2>
                
                <div className="space-y-6">
                  {/* Height */}
                  <div>
                    <label className="text-sm text-muted-foreground block mb-2">Height *</label>
                    <div className="flex gap-3">
                      <div className="flex-1">
                        <Input
                          type="number"
                          placeholder="Feet"
                          value={height.feet}
                          onChange={(e) => setHeight(prev => ({...prev, feet: e.target.value}))}
                          className="bg-background border-white/10"
                          min="4"
                          max="7"
                        />
                        <span className="text-xs text-muted-foreground mt-1 block">Feet</span>
                      </div>
                      <div className="flex-1">
                        <Input
                          type="number"
                          placeholder="Inches"
                          value={height.inches}
                          onChange={(e) => setHeight(prev => ({...prev, inches: e.target.value}))}
                          className="bg-background border-white/10"
                          min="0"
                          max="11"
                        />
                        <span className="text-xs text-muted-foreground mt-1 block">Inches</span>
                      </div>
                    </div>
                  </div>

                  {/* Weight */}
                  <div>
                    <label className="text-sm text-muted-foreground block mb-2">Weight (optional)</label>
                    <Input
                      type="number"
                      placeholder="Weight in lbs"
                      value={weight}
                      onChange={(e) => setWeight(e.target.value)}
                      className="bg-background border-white/10 max-w-xs"
                    />
                    <span className="text-xs text-muted-foreground mt-1 block">Helps improve accuracy</span>
                  </div>

                  {/* Fit Preference */}
                  <div>
                    <label className="text-sm text-muted-foreground block mb-2">Fit Preference *</label>
                    <Select value={fitPreference} onValueChange={setFitPreference}>
                      <SelectTrigger className="w-full max-w-xs bg-background border-white/10">
                        <SelectValue placeholder="Select fit" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Slim">Slim Fit</SelectItem>
                        <SelectItem value="Classic">Classic Fit</SelectItem>
                        <SelectItem value="Relaxed">Relaxed Fit</SelectItem>
                      </SelectContent>
                    </Select>
                    <span className="text-xs text-muted-foreground mt-1 block">
                      {fitPreference === "Slim" && "Fitted, modern cut with minimal ease"}
                      {fitPreference === "Classic" && "Traditional, comfortable fit"}
                      {fitPreference === "Relaxed" && "Generous ease for comfort"}
                    </span>
                  </div>

                  {/* Optional: Link to Client */}
                  <div>
                    <label className="text-sm text-muted-foreground block mb-2">Link to Client (optional)</label>
                    <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                      <SelectTrigger className="w-full bg-background border-white/10">
                        <SelectValue placeholder="Select a client to save measurements to" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {clients.map(client => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.name} {client.email && `(${client.email})`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <span className="text-xs text-muted-foreground mt-1 block">Measurements will be saved to their CRM profile</span>
                  </div>
                </div>
              </div>

              <Button 
                onClick={() => setStep(2)} 
                disabled={!canProceedStep1}
                className="w-full bg-primary"
              >
                Continue to Photos
              </Button>
            </motion.div>
          )}

          {/* Step 2: Photo Upload */}
          {step === 2 && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              {/* Photo Instructions */}
              <div className="bg-amber-500/10 border border-amber-500/30 p-4 rounded-lg mb-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="text-amber-400 mt-0.5 flex-shrink-0" size={20} />
                  <div>
                    <h4 className="font-medium text-amber-400 mb-1">Photo Requirements</h4>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li>• Wear <span className="text-foreground">tight-fitting clothing</span> (t-shirt + fitted pants)</li>
                      <li>• Stand in a <span className="text-foreground">neutral stance</span> with arms slightly away from body</li>
                      <li>• Have someone take photos from <span className="text-foreground">8-10 feet away</span></li>
                      <li>• Camera should be at <span className="text-foreground">waist height</span></li>
                      <li>• Use a <span className="text-foreground">plain background</span></li>
                      <li>• <span className="text-foreground text-amber-300">No wide-angle or mirror photos</span></li>
                    </ul>
                    <Button variant="link" className="text-amber-400 p-0 h-auto mt-2" onClick={() => setShowGuide(true)}>
                      View detailed photo guide →
                    </Button>
                  </div>
                </div>
              </div>

              {/* Photo Upload Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {/* Front Photo */}
                <div className="bg-card border border-white/10 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium">Front View *</h4>
                    {frontPreview && <Check className="text-green-400" size={18} />}
                  </div>
                  <div 
                    className={`aspect-[3/4] border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer transition-colors ${
                      frontPreview ? "border-green-500/50" : "border-white/20 hover:border-primary/50"
                    }`}
                    onClick={() => frontInputRef.current?.click()}
                    style={frontPreview ? { backgroundImage: `url(${frontPreview})`, backgroundSize: "cover", backgroundPosition: "center" } : {}}
                  >
                    {!frontPreview && (
                      <>
                        <Camera size={32} className="text-muted-foreground mb-2" />
                        <span className="text-sm text-muted-foreground">Click to upload</span>
                      </>
                    )}
                  </div>
                  <input 
                    ref={frontInputRef}
                    type="file" 
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden" 
                    onChange={(e) => handleImageUpload(e, "front")}
                  />
                </div>

                {/* Side Photo */}
                <div className="bg-card border border-white/10 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium">Side View *</h4>
                    {sidePreview && <Check className="text-green-400" size={18} />}
                  </div>
                  <div 
                    className={`aspect-[3/4] border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer transition-colors ${
                      sidePreview ? "border-green-500/50" : "border-white/20 hover:border-primary/50"
                    }`}
                    onClick={() => sideInputRef.current?.click()}
                    style={sidePreview ? { backgroundImage: `url(${sidePreview})`, backgroundSize: "cover", backgroundPosition: "center" } : {}}
                  >
                    {!sidePreview && (
                      <>
                        <Camera size={32} className="text-muted-foreground mb-2" />
                        <span className="text-sm text-muted-foreground">Click to upload</span>
                      </>
                    )}
                  </div>
                  <input 
                    ref={sideInputRef}
                    type="file" 
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden" 
                    onChange={(e) => handleImageUpload(e, "side")}
                  />
                </div>

                {/* Back Photo (Optional) */}
                <div className="bg-card border border-white/10 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-muted-foreground">Back View</h4>
                    {backPreview && <Check className="text-green-400" size={18} />}
                  </div>
                  <div 
                    className={`aspect-[3/4] border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer transition-colors ${
                      backPreview ? "border-green-500/50" : "border-white/20 hover:border-primary/50"
                    }`}
                    onClick={() => backInputRef.current?.click()}
                    style={backPreview ? { backgroundImage: `url(${backPreview})`, backgroundSize: "cover", backgroundPosition: "center" } : {}}
                  >
                    {!backPreview && (
                      <>
                        <Camera size={32} className="text-muted-foreground mb-2" />
                        <span className="text-xs text-muted-foreground text-center px-2">Optional - improves accuracy</span>
                      </>
                    )}
                  </div>
                  <input 
                    ref={backInputRef}
                    type="file" 
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden" 
                    onChange={(e) => handleImageUpload(e, "back")}
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                  Back
                </Button>
                <Button 
                  onClick={handleAnalyze} 
                  disabled={!canProceedStep2 || analyzing}
                  className="flex-1 bg-primary"
                >
                  {analyzing ? (
                    <>
                      <Loader2 className="animate-spin mr-2" size={16} />
                      Analyzing...
                    </>
                  ) : (
                    "Analyze Photos"
                  )}
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 3: Results */}
          {step === 3 && results && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="print:p-0">
              {/* Summary Card */}
              <div className="bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/30 p-6 rounded-lg mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-serif text-xl">Your MTM Measurements</h2>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-1 rounded border ${getConfidenceBadgeColor(results.confidence_level)}`}>
                      {results.confidence_level} Confidence
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-4 text-center">
                  <div>
                    <span className="text-2xl font-mono text-primary">{results.body_type}</span>
                    <span className="text-xs text-muted-foreground block mt-1">Body Type</span>
                  </div>
                  <div>
                    <span className="text-2xl font-mono text-primary">{results.drop}&quot;</span>
                    <span className="text-xs text-muted-foreground block mt-1">Drop</span>
                  </div>
                  <div>
                    <span className="text-2xl font-mono text-primary">{Math.floor(results.height_inches / 12)}&apos;{results.height_inches % 12}&quot;</span>
                    <span className="text-xs text-muted-foreground block mt-1">Height</span>
                  </div>
                  <div>
                    <span className="text-2xl font-mono text-primary">{results.fit_preference}</span>
                    <span className="text-xs text-muted-foreground block mt-1">Fit</span>
                  </div>
                </div>
              </div>

              {/* Tailor Review Warning */}
              {results.tailor_review_required && (
                <div className="bg-amber-500/10 border border-amber-500/30 p-4 rounded-lg mb-6">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="text-amber-400 mt-0.5" size={20} />
                    <div>
                      <h4 className="font-medium text-amber-400 mb-1">Tailor Review Recommended</h4>
                      <p className="text-sm text-muted-foreground">
                        Based on confidence level ({results.confidence_score}%), we recommend an in-house tailor verify these measurements before cutting.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Jacket Measurements */}
              <div className="bg-card border border-white/10 rounded-lg mb-4">
                <div className="bg-blue-600 text-white px-4 py-2 font-medium text-sm rounded-t-lg">
                  JACKET MEASUREMENTS ({results.fit_preference} fit with MTM ease)
                </div>
                <div className="p-4 grid grid-cols-2 gap-x-6">
                  <MeasurementDisplay label="Chest" value={results.chest} />
                  <MeasurementDisplay label="Waist/Stomach" value={results.stomach} />
                  <MeasurementDisplay label="Seat/Hip" value={results.seat} />
                  <MeasurementDisplay label="Shoulder Width" value={results.shoulder_width} />
                  <MeasurementDisplay label="Bicep" value={results.bicep} />
                  <MeasurementDisplay label="Wrist" value={results.wrist} />
                  <MeasurementDisplay label="Sleeve Right" value={results.sleeve_length_right} />
                  <MeasurementDisplay label="Sleeve Left" value={results.sleeve_length_left} />
                  <MeasurementDisplay label="Back Length" value={results.back_length} />
                  <MeasurementDisplay label="Front Length" value={results.front_length} />
                  <MeasurementDisplay label="Neck" value={results.neck} />
                  <MeasurementDisplay label="First Button" value={results.first_button_position} />
                </div>
              </div>

              {/* Trouser Measurements */}
              <div className="bg-card border border-white/10 rounded-lg mb-4">
                <div className="bg-red-700 text-white px-4 py-2 font-medium text-sm rounded-t-lg">
                  TROUSER MEASUREMENTS ({results.fit_preference} fit with MTM ease)
                </div>
                <div className="p-4 grid grid-cols-2 gap-x-6">
                  <MeasurementDisplay label="Waist" value={results.waist} />
                  <MeasurementDisplay label="Hip/Seat" value={results.hip} />
                  <MeasurementDisplay label="Thigh" value={results.thigh} />
                  <MeasurementDisplay label="Knee" value={results.knee} />
                  <MeasurementDisplay label="Bottom/Hem" value={results.bottom} />
                  <MeasurementDisplay label="Outseam" value={results.outseam} />
                  <MeasurementDisplay label="Inseam" value={results.inseam} />
                  <MeasurementDisplay label="U-Rise" value={results.rise} />
                </div>
              </div>

              {/* AI Observations */}
              {(results.posture_notes || results.ai_notes) && (
                <div className="bg-card border border-white/10 rounded-lg mb-6 p-4">
                  <h4 className="font-medium mb-3">AI Tailor Observations</h4>
                  {results.posture_notes && (
                    <div className="mb-2">
                      <span className="text-sm font-medium text-foreground">Posture Analysis:</span>
                      <p className="text-sm text-muted-foreground mt-1">{results.posture_notes}</p>
                    </div>
                  )}
                  {results.ai_notes && (
                    <div>
                      <span className="text-sm font-medium text-foreground">Tailor Notes:</span>
                      <p className="text-sm text-muted-foreground mt-1">{results.ai_notes}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 flex-wrap print:hidden">
                <Button variant="outline" onClick={() => setStep(2)} className="flex-1">
                  Retake Photos
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setShowSaveToClient(true)}
                  className="flex-1"
                >
                  <Users size={16} className="mr-2" />
                  Save to Client
                </Button>
                <Button 
                  variant="outline" 
                  onClick={handlePrint}
                  className="flex-1"
                >
                  <Printer size={16} className="mr-2" />
                  Print
                </Button>
                <Button 
                  onClick={() => navigate("/booking")} 
                  className="flex-1 bg-primary"
                >
                  Book Consultation
                </Button>
              </div>

              <p className="text-xs text-muted-foreground text-center mt-4 print:hidden">
                These AI measurements are recommendations, not final cutting instructions. 
                Final approval must be performed by an in-house tailor.
              </p>
            </motion.div>
          )}
        </div>
      </section>

      {/* Photo Guide Dialog */}
      <Dialog open={showGuide} onOpenChange={setShowGuide}>
        <DialogContent className="bg-card border-white/10 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif">Photo Guide for AI Measurement</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-6">
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="aspect-[3/4] bg-blue-500/10 border border-blue-500/30 rounded-lg flex items-center justify-center mb-2">
                  <User size={48} className="text-blue-400" />
                </div>
                <span className="text-sm font-medium">Front View</span>
                <p className="text-xs text-muted-foreground">Face camera directly</p>
              </div>
              <div className="text-center">
                <div className="aspect-[3/4] bg-green-500/10 border border-green-500/30 rounded-lg flex items-center justify-center mb-2">
                  <User size={48} className="text-green-400 rotate-90" />
                </div>
                <span className="text-sm font-medium">Side View</span>
                <p className="text-xs text-muted-foreground">90° from camera</p>
              </div>
              <div className="text-center">
                <div className="aspect-[3/4] bg-purple-500/10 border border-purple-500/30 rounded-lg flex items-center justify-center mb-2">
                  <User size={48} className="text-purple-400 rotate-180" />
                </div>
                <span className="text-sm font-medium">Back View</span>
                <p className="text-xs text-muted-foreground">Optional - improves accuracy</p>
              </div>
            </div>
            
            <div className="space-y-3">
              <h4 className="font-medium">Requirements for Best Results:</h4>
              <ul className="text-sm space-y-2 text-muted-foreground">
                <li className="flex items-start gap-2">
                  <Check size={16} className="text-green-400 mt-0.5 flex-shrink-0" />
                  <span>Wear <strong className="text-foreground">tight-fitting clothing</strong> (fitted t-shirt and pants, not baggy)</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check size={16} className="text-green-400 mt-0.5 flex-shrink-0" />
                  <span>Stand <strong className="text-foreground">naturally with arms slightly away</strong> from your body</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check size={16} className="text-green-400 mt-0.5 flex-shrink-0" />
                  <span>Photographer should be <strong className="text-foreground">8-10 feet away</strong> at <strong className="text-foreground">waist height</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <Check size={16} className="text-green-400 mt-0.5 flex-shrink-0" />
                  <span>Use <strong className="text-foreground">good lighting</strong> and a <strong className="text-foreground">plain background</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <Check size={16} className="text-green-400 mt-0.5 flex-shrink-0" />
                  <span>Ensure <strong className="text-foreground">full body is visible</strong> in frame (head to feet)</span>
                </li>
                <li className="flex items-start gap-2">
                  <AlertCircle size={16} className="text-amber-400 mt-0.5 flex-shrink-0" />
                  <span className="text-amber-400"><strong>No wide-angle lenses</strong> or mirror selfies (causes distortion)</span>
                </li>
              </ul>
            </div>

            <div className="bg-primary/10 border border-primary/30 p-4 rounded-lg">
              <p className="text-sm text-primary">
                <strong>Professional Note:</strong> These photos will be analyzed by our AI measurement system and reviewed by an in-house tailor. Better photos = more accurate measurements.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Save to Client Dialog */}
      <Dialog open={showSaveToClient} onOpenChange={setShowSaveToClient}>
        <DialogContent className="bg-card border-white/10">
          <DialogHeader>
            <DialogTitle className="font-serif">Save to Client Profile</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-muted-foreground">
              Select a client to save these AI measurements to their CRM profile.
            </p>
            <Select value={selectedClientId} onValueChange={setSelectedClientId}>
              <SelectTrigger className="w-full bg-background border-white/10">
                <SelectValue placeholder="Select a client" />
              </SelectTrigger>
              <SelectContent>
                {clients.map(client => (
                  <SelectItem key={client.id} value={client.id}>
                    {client.name} {client.email && `(${client.email})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setShowSaveToClient(false)} className="flex-1">
                Cancel
              </Button>
              <Button 
                onClick={handleSaveToClient} 
                disabled={!selectedClientId || saving}
                className="flex-1 bg-primary"
              >
                {saving ? (
                  <>
                    <Loader2 className="animate-spin mr-2" size={16} />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={16} className="mr-2" />
                    Save
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AIMeasurementPage;
